import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getRevenueReport, revenueReportCsvUrl } from '../../services/api';
import './PeriodReportModal.css';

const money = (v) => `${Math.round(v || 0).toLocaleString('ru-RU')} ₽`;
const iso = (d) => {
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};
const human = (s) => {
  const [y, m, d] = s.split('-');
  return `${d}.${m}.${y}`;
};

// Готовые периоды — то, что владелец спрашивает чаще всего. Считаются в
// браузере в локальном времени, сервер получает уже конкретные даты.
const presets = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const day = (today.getDay() + 6) % 7; // пн = 0
  const monday = new Date(today); monday.setDate(today.getDate() - day);
  const prevMonday = new Date(monday); prevMonday.setDate(monday.getDate() - 7);
  const prevSunday = new Date(monday); prevSunday.setDate(monday.getDate() - 1);
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const prevMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const prevMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
  const last30 = new Date(today); last30.setDate(today.getDate() - 29);
  const yearStart = new Date(today.getFullYear(), 0, 1);
  return [
    { key: 'week', label: 'Эта неделя', from: monday, to: today },
    { key: 'prev_week', label: 'Прошлая неделя', from: prevMonday, to: prevSunday },
    { key: 'month', label: 'Этот месяц', from: monthStart, to: today },
    { key: 'prev_month', label: 'Прошлый месяц', from: prevMonthStart, to: prevMonthEnd },
    { key: 'last30', label: '30 дней', from: last30, to: today },
    { key: 'year', label: 'С начала года', from: yearStart, to: today },
  ].map(p => ({ ...p, from: iso(p.from), to: iso(p.to) }));
};

const Delta = ({ value, suffix }) => {
  if (!value || value.pct === null || value.pct === undefined) return null;
  const cls = value.pct > 0 ? 'up' : (value.pct < 0 ? 'down' : 'flat');
  const arrow = value.pct > 0 ? '▲' : (value.pct < 0 ? '▼' : '•');
  return <span className={`pr-delta ${cls}`}>{arrow} {Math.abs(value.pct).toFixed(1)}% {suffix}</span>;
};

const Tile = ({ label, value, prev, lastYear }) => (
  <div className="pr-tile">
    <div className="pr-tile-label">{label}</div>
    <div className="pr-tile-value">{value}</div>
    <div className="pr-tile-deltas">
      <Delta value={prev} suffix="к пред. периоду" />
      <Delta value={lastYear} suffix="к прошлому году" />
    </div>
  </div>
);

const PeriodReportModal = ({ onClose }) => {
  const options = useMemo(presets, []);
  const [range, setRange] = useState({ key: 'month', from: options[2].from, to: options[2].to });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (from, to) => {
    setLoading(true);
    setError('');
    try {
      setReport(await getRevenueReport(from, to));
    } catch (e) {
      setReport(null);
      setError(e?.response?.data?.error || 'Не удалось построить отчёт');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(range.from, range.to); }, [load, range.from, range.to]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const pick = (p) => setRange({ key: p.key, from: p.from, to: p.to });
  const setCustom = (field) => (e) => {
    const value = e.target.value;
    if (value) setRange(prev => ({ ...prev, key: 'custom', [field]: value }));
  };

  const maxWeekday = report ? Math.max(...report.by_weekday.map(w => w.avg), 1) : 1;
  const cur = report?.current;

  return (
    <div className="ui-modal" onClick={onClose}>
      <div className="ui-modal-box pr-box" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true"
           aria-labelledby="pr-title">
        <div className="ui-modal-head">
          <h2 id="pr-title">Отчёт о выручке</h2>
          <button type="button" className="ui-btn-ghost" onClick={onClose}>Закрыть</button>
        </div>

        <div className="pr-presets" role="group" aria-label="Период">
          {options.map(p => (
            <button key={p.key} type="button" className={`ui-chip pr-chip ${range.key === p.key ? 'active' : ''}`}
                    onClick={() => pick(p)}>
              {p.label}
            </button>
          ))}
        </div>

        <div className="pr-dates">
          <label>
            <span>С</span>
            <input type="date" value={range.from} max={range.to} onChange={setCustom('from')} />
          </label>
          <label>
            <span>по</span>
            <input type="date" value={range.to} min={range.from} onChange={setCustom('to')} />
          </label>
          {report && (
            <a className="ui-btn-ghost pr-csv" href={revenueReportCsvUrl(range.from, range.to)} download>
              Скачать CSV
            </a>
          )}
        </div>

        {loading && !report && <p className="ui-dim">Считаем…</p>}
        {error && <p className="ui-hint-error">{error}</p>}

        {report && (
          <div className={`pr-body ${loading ? 'is-loading' : ''}`}>
            <div className="pr-tiles">
              <Tile label="Выручка" value={money(cur.revenue)}
                    prev={report.vs_previous?.revenue} lastYear={report.vs_last_year?.revenue} />
              <Tile label="Чеков" value={cur.orders.toLocaleString('ru-RU')}
                    prev={report.vs_previous?.orders} lastYear={report.vs_last_year?.orders} />
              <Tile label="Средний чек" value={money(cur.avg_check)}
                    prev={report.vs_previous?.avg_check} lastYear={report.vs_last_year?.avg_check} />
              <Tile label="В среднем за день" value={money(cur.avg_per_day)} />
            </div>

            <p className="pr-note">
              {human(cur.from)} — {human(cur.to)} · дней с продажами: {cur.days_with_sales} из {report.days}.
              {report.previous && <> Предыдущий период: {human(report.previous.from)} — {human(report.previous.to)}, {money(report.previous.revenue)}.</>}
              {report.last_year && <> Год назад: {money(report.last_year.revenue)}.</>}
              {!report.last_year && report.history_from && <> Сравнения с прошлым годом нет: история загружена с {human(report.history_from)}.</>}
              {report.partial_day && <> Сегодня ещё не закончился: в итогах он учтён по текущий момент, в средних и в лучшем/худшем дне — нет.</>}
            </p>

            {report.best_day && report.best_day.date !== report.worst_day?.date && (
              <div className="pr-extremes">
                <div><span>Лучший день</span><strong>{human(report.best_day.date)}, {report.best_day.weekday} — {money(report.best_day.revenue)}</strong></div>
                <div><span>Худший день</span><strong>{human(report.worst_day.date)}, {report.worst_day.weekday} — {money(report.worst_day.revenue)}</strong></div>
              </div>
            )}

            <h3 className="pr-subtitle">Средняя выручка по дням недели</h3>
            <div className="pr-weekdays">
              {report.by_weekday.map(w => (
                <div key={w.weekday} className="pr-weekday">
                  <span className="pr-weekday-name">{w.name}</span>
                  <div className="pr-weekday-track">
                    <div className="pr-weekday-bar" style={{ width: `${(w.avg / maxWeekday) * 100}%` }} />
                  </div>
                  <span className="pr-weekday-value">{w.days ? money(w.avg) : '—'}</span>
                </div>
              ))}
            </div>

            <h3 className="pr-subtitle">По дням</h3>
            <div className="pr-table-wrap">
              <table className="pr-table">
                <thead>
                  <tr>
                    <th>Дата</th>
                    <th className="num">Выручка</th>
                    <th className="num">Чеков</th>
                    <th className="num">Ср. чек</th>
                    <th className="num">Год назад</th>
                  </tr>
                </thead>
                <tbody>
                  {[...report.daily].reverse().map(d => (
                    <tr key={d.date} className={d.revenue ? '' : 'pr-empty-day'}>
                      <td>{human(d.date)} <span className="ui-dim">{d.partial ? 'сегодня, идёт' : d.weekday}</span></td>
                      <td className="num">{d.revenue ? money(d.revenue) : '—'}</td>
                      <td className="num">{d.orders || '—'}</td>
                      <td className="num">{d.avg_check ? money(d.avg_check) : '—'}</td>
                      <td className="num ui-dim">{d.last_year_revenue ? money(d.last_year_revenue) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PeriodReportModal;
